/**
 * Small, fast, seedable PRNG (mulberry32). Track generation must be
 * reproducible from a seed – required for debugging and for the later
 * server-side run validation (anti-cheat replays).
 */
export class Random {
  private state: number;

  constructor(readonly seed: number) {
    this.state = seed >>> 0;
  }

  /**
   * Independent seed for a sub-stream (splitmix-style mixing). Separate streams
   * keep e.g. cosmetic changes from shifting gameplay generation.
   */
  static deriveSeed(seed: number, stream: number): number {
    let z = (seed + Math.imul(stream, 0x9e3779b9)) >>> 0;
    z = Math.imul(z ^ (z >>> 16), 0x85ebca6b) >>> 0;
    z = Math.imul(z ^ (z >>> 13), 0xc2b2ae35) >>> 0;
    return (z ^ (z >>> 16)) >>> 0;
  }

  static randomSeed(): number {
    return (Math.random() * 0xffffffff) >>> 0;
  }

  /** Uniform float in [0, 1). */
  next(): number {
    this.state = (this.state + 0x6d2b79f5) >>> 0;
    let t = this.state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  range(min: number, max: number): number {
    return min + (max - min) * this.next();
  }

  /** Integer in [min, max] (inclusive). */
  int(min: number, max: number): number {
    return min + Math.floor(this.next() * (max - min + 1));
  }

  chance(probability: number): boolean {
    return this.next() < probability;
  }

  pick<T>(items: readonly T[]): T {
    if (items.length === 0) throw new Error('Random.pick: empty list');
    return items[Math.floor(this.next() * items.length)] as T;
  }

  /** Picks by weight; entries with weight <= 0 are never chosen. Returns undefined for an empty/zero list. */
  weighted<T>(entries: readonly (readonly [T, number])[]): T | undefined {
    let total = 0;
    for (const [, weight] of entries) total += Math.max(0, weight);
    if (total <= 0) return undefined;
    let roll = this.next() * total;
    for (const [item, weight] of entries) {
      if (weight <= 0) continue;
      roll -= weight;
      if (roll < 0) return item;
    }
    return entries[entries.length - 1]?.[0];
  }

  shuffle<T>(items: T[]): T[] {
    for (let i = items.length - 1; i > 0; i--) {
      const j = Math.floor(this.next() * (i + 1));
      [items[i], items[j]] = [items[j] as T, items[i] as T];
    }
    return items;
  }
}
